import React, {useCallback, useContext} from 'react';
import {jsonToCSV} from 'react-papaparse';

import moment from 'moment/moment';
import {DOWNLOAD_FAILED_MESSAGE, DOWNLOAD_STACKOVERFLOW_MESSAGE, DOWNLOAD_SUCCESS_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {LocaleContext} from '@pb/kit-context-locale';

import {ChunkedDownloadRequestNotification} from './chuncked-download-request-notification';
import {chunkArray} from './utils';

import type {GetData} from './typedef';
import type {ReactNode} from 'react';


type Props<Data> = {
    children: (handler: () => void) => ReactNode,

    ids?: number[],
    taskName: string,
    fileName: string,
    fileHeaders: Record<string, string>,
    getData: GetData<Data>,
    chunkSize: number,

    onSuccess?: () => void,
    onError?: () => void,

    stackOverflowMessage?: string,
};

export const ChunkedDownloadTool = <Data, >(props: Props<Data>) => {
    const {
        children,

        taskName,
        fileName,
        fileHeaders,
        getData,
        chunkSize,

        onSuccess,
        onError,

        stackOverflowMessage = DOWNLOAD_STACKOVERFLOW_MESSAGE,
    } = props;
    const createBackgroundTask = useBackgroundTaskManager();
    const {locale} = useContext(LocaleContext);

    const generateFile = useCallback((fileData: Data[]) => {
        const dataWithHeaders = [fileHeaders, ...fileData];
        const csv = jsonToCSV(dataWithHeaders, {
            delimiter: ',',
            header: false,
            newline: '\r\n',
            quotes: true,
            skipEmptyLines: true,
        });

        const blob = new Blob([csv], {type: 'text/csv'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        link.download = `${fileName}_${moment().format(
            mapDateFormat(locale.dateFormat, 'FILE_NAME', 'DATE_TIME'),
        )}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [fileName, fileHeaders, locale.dateFormat]);

    const handler = useCallback((ids?: number[]) => {
        const entityIds = ids || props.ids;

        if (entityIds) {
            const downloadTask = createBackgroundTask(
                taskName,
                <ChunkedDownloadRequestNotification<Data>
                    chunks={chunkArray(entityIds, chunkSize)}
                    getData={getData}
                    onDone={(data, error) => {
                        if (!downloadTask) {
                            return;
                        }

                        const {completeSuccess, completeFailure} = downloadTask;

                        if (!error) {
                            generateFile(data);
                            completeSuccess(<b>{DOWNLOAD_SUCCESS_MESSAGE}</b>);
                            onSuccess?.();
                        } else {
                            completeFailure(<b>{DOWNLOAD_FAILED_MESSAGE}</b>);
                            onError?.();
                        }
                    }}
                />,
                {
                    maxStack: 1,
                    stackOverflowMessage,
                    cancelable: true,
                    cancelMessage: 'The downloading is canceled',
                },
            );
        }
    }, [
        props.ids,
        chunkSize,
        generateFile,
        taskName,
        getData,
        createBackgroundTask,
        onSuccess,
        onError,
        stackOverflowMessage,
    ]);

    return (
        <div>
            {children(handler)}
        </div>
    );
};
